import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'contexts/AlertContext';
import { BlockedUser } from 'components/settings/BlockedUserItem';
import { BlockRepository } from 'repository/blockRepository';
import { AppScreen, Button, SearchInput, Section, SectionHeader } from 'components/ds';
import styles from './BlockedUsers.module.css';

export const BlockedUsers: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { showConfirm, showAlert } = useAlert();
    const [blockedUsers, setBlockedUsers] = useState<BlockedUser[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);

    const loadBlocks = useCallback(async () => {
        setLoading(true);
        try {
            const res: any = await BlockRepository.onGetMyBlocks();
            const list: BlockedUser[] = res?.list ?? res?.data?.list ?? [];
            setBlockedUsers(list);
        } catch (e) {
            console.warn('차단 목록 조회 실패', e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadBlocks();
    }, [loadBlocks]);

    const handleUnblock = async (user: BlockedUser) => {
        const targetName = user.nickname || `User #${user.blockedUserNo}`;
        const confirmed = await showConfirm({
            message: t('blockedUsers.unblockConfirm', { name: targetName })
        });
        if (!confirmed) return;
        try {
            await BlockRepository.onDeleteBlock(user.blockedUserNo);
            setBlockedUsers(prev => prev.filter(u => u.blockedUserNo !== user.blockedUserNo));
            window.dispatchEvent(new CustomEvent('dokocrew:unblockChange', {
                detail: { userNo: user.blockedUserNo },
            }));
        } catch (e) {
            console.warn('차단 해제 실패', e);
            showAlert({ message: t('blockedUsers.unblockFail') });
        }
    };

    const q = searchQuery.trim().toLowerCase();
    const filteredUsers = q
        ? blockedUsers.filter(u =>
              String(u.blockedUserNo).includes(q) ||
              (u.nickname ?? '').toLowerCase().includes(q) ||
              (u.reason ?? '').toLowerCase().includes(q)
          )
        : blockedUsers;

    const formatDate = (iso?: string | null): string => {
        if (!iso) return '';
        const d = new Date(iso);
        if (isNaN(d.getTime())) return '';
        return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
    };

    return (
        <AppScreen title={t('blockedUsers.title')} bar="sub" onBack={() => navigate('/settings')}>
            <Section spacing="tight">
                <SearchInput
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder={t('blockedUsers.searchPlaceholder')}
                    clearLabel={t('common.close')}
                />
                <p className={styles.info}>{t('blockedUsers.info')}</p>
            </Section>

            <Section divided last>
                <SectionHeader
                    title={t('blockedUsers.listTitle')}
                    count={blockedUsers.length}
                />
                {loading ? (
                    <p className={styles.state}>{t('blockedUsers.loading')}</p>
                ) : filteredUsers.length === 0 ? (
                    <p className={styles.state}>
                        {searchQuery ? t('blockedUsers.noResults') : t('blockedUsers.noBlocked')}
                    </p>
                ) : (
                    <div className={styles.list}>
                        {filteredUsers.map(user => {
                            const name = user.nickname || t('blockedUsers.userNo', { no: user.blockedUserNo });
                            const date = formatDate(user.createdAt);
                            return (
                                <div key={user.blockedUserNo} className={styles.row}>
                                    <span className={styles.avatar}>
                                        {user.profileUrl
                                            ? <img src={user.profileUrl} alt="" />
                                            : name.charAt(0).toUpperCase()}
                                    </span>
                                    <span className={styles.body}>
                                        <span className={styles.name}>{name}</span>
                                        <span className={styles.meta}>
                                            {date ? t('blockedUsers.blockedAt', { date }) : t('blockedUsers.blockedNoDate')}
                                            {user.reason ? ` · ${user.reason}` : ''}
                                        </span>
                                    </span>
                                    <Button variant="secondary" size="sm" onClick={() => handleUnblock(user)}>
                                        {t('blockedUsers.unblock')}
                                    </Button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Section>
        </AppScreen>
    );
};
